import React, { useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  Clock3,
  Edit3,
  ShieldCheck,
  Sparkles,
} from "lucide-react";

import Layout from "../components/Layout";
import InspectionSummaryPanel from "../components/compliance/InspectionSummaryPanel";
import ComplianceModulesHub from "../components/compliance/ComplianceModulesHub";
import CFOInspectionPreparationPanel from "../components/compliance/CFOInspectionPreparationPanel";
import ReceivedInspectionsPanel from "../components/compliance/ReceivedInspectionsPanel";
import TeamAdvertisingPanel from "../components/compliance/TeamAdvertisingPanel";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import api from "../services/api";
import { trackUxInteraction } from "../services/uxTelemetry";

const EMPTY_PROFILE = {
  establishment_type: "individual_office",
  consultory_count: 1,
  sedation_type: "none",
  radiology_type: "none",
  processing_type: "internal",
  autoclave_has_vacuum_pump: false,
  provides_external_care: false,
  has_prosthesis_lab: false,
  cfo_inspection_type: null,
  cfo_service_type: "standard",
};

const STATUS_OPTIONS = [
  {
    value: "not_started",
    label: "Ainda não",
    className: "bg-gray-100 text-gray-700",
  },
  {
    value: "in_progress",
    label: "Em andamento",
    className: "bg-amber-100 text-amber-800",
  },
  {
    value: "compliant",
    label: "Concluído",
    className: "bg-emerald-100 text-emerald-800",
  },
  {
    value: "not_applicable",
    label: "Não se aplica",
    className: "bg-blue-100 text-blue-800",
  },
];

const CATEGORY_LABELS = {
  regularization: "Regularização",
  documents: "Documentos",
  people: "Equipe",
  routine: "Rotinas",
  equipment: "Estrutura e equipamentos",
  safety: "Segurança",
};

const Choice = ({ active, title, description, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full rounded-xl border p-4 text-left transition ${active ? "border-purple-500 bg-purple-50 ring-1 ring-purple-200" : "border-gray-200 bg-white hover:border-purple-200"}`}
  >
    <span className="flex items-start gap-3">
      <span
        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${active ? "border-purple-600 bg-purple-600 text-white" : "border-gray-300"}`}
      >
        {active && <Check className="h-3.5 w-3.5" />}
      </span>
      <span>
        <span className="block font-medium text-gray-900">{title}</span>
        {description && (
          <span className="mt-1 block text-sm text-gray-500">
            {description}
          </span>
        )}
      </span>
    </span>
  </button>
);

const CompliancePage = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const permissions = useMemo(
    () => new Set(user?.permissions || []),
    [user?.permissions],
  );
  const canManage = permissions.has("compliance.manage");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const [showPlan, setShowPlan] = useState(false);
  const [showSpecialRoutes, setShowSpecialRoutes] = useState(false);
  const [showOtherControls, setShowOtherControls] = useState(false);
  const [focusedModule, setFocusedModule] = useState(null);
  const [focusedModuleRequest, setFocusedModuleRequest] = useState(0);

  const load = async () => {
    try {
      const response = await api.get("/compliance");
      setData(response.data);
      if (response.data?.profile)
        setProfile({ ...EMPTY_PROFILE, ...response.data.profile });
    } catch (error) {
      showToast(
        error?.response?.data?.detail || "Não foi possível carregar a central.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const saveProfile = async () => {
    setSaving(true);
    try {
      const payload = {
        ...profile,
        consultory_count: Number(profile.consultory_count || 1),
      };
      const response = await api.put("/compliance/profile", payload);
      setData(response.data);
      setWizardOpen(false);
      setStep(0);
      showToast("Plano da clínica preparado com sucesso.");
    } catch (error) {
      showToast(
        error?.response?.data?.detail ||
          "Não foi possível salvar o diagnóstico.",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (item, status) => {
    try {
      const response = await api.patch(`/compliance/checklist/${item.id}`, {
        status,
        note: item.note || null,
      });
      setData(response.data);
      showToast("Progresso atualizado.");
    } catch (error) {
      showToast(
        error?.response?.data?.detail || "Não foi possível atualizar o item.",
        "error",
      );
    }
  };

  const startWizard = () => {
    const sourceProfile = data?.profile || data?.prefill_profile;
    const selectedServiceType = sourceProfile?.cfo_service_type || "standard";
    setProfile(
      sourceProfile ? { ...EMPTY_PROFILE, ...sourceProfile } : EMPTY_PROFILE,
    );
    setShowSpecialRoutes(selectedServiceType !== "standard");
    setStep(0);
    setWizardOpen(true);
  };

  const openActionModule = (module) => {
    trackUxInteraction({
      event_name: "compliance_guided_action_opened",
      module_id: "compliance",
      source: "compliance_guided",
    });
    setFocusedModule(module);
    setFocusedModuleRequest((value) => value + 1);
    setShowOtherControls(true);
  };

  useEffect(() => {
    if (!focusedModuleRequest) return undefined;

    let secondFrame;
    const firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => {
        const target =
          document.getElementById("compliance-focused-module") ||
          document.getElementById("compliance-controls");
        target?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    });

    return () => {
      window.cancelAnimationFrame(firstFrame);
      if (secondFrame) window.cancelAnimationFrame(secondFrame);
    };
  }, [focusedModuleRequest]);

  if (loading)
    return (
      <Layout>
        <div className="mx-auto max-w-4xl py-12 text-gray-500">
          Preparando sua central...
        </div>
      </Layout>
    );

  if (wizardOpen) {
    const steps = ["Sobre a clínica", "Atendimentos", "Rotina"];
    const continuingPublicAssessment = Boolean(data?.pending_assessment);
    return (
      <Layout>
        <div className="mx-auto max-w-3xl py-4 sm:py-8">
          <button
            type="button"
            onClick={() => setWizardOpen(false)}
            className="mb-5 flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4" /> Voltar
          </button>
          <div className="rounded-2xl bg-white p-5 shadow-sm sm:p-8">
            <div className="mb-7">
              <div className="mb-3 flex items-center justify-between text-sm">
                <span className="font-medium text-purple-700">
                  {continuingPublicAssessment
                    ? "Confirmação final"
                    : "Diagnóstico rápido"}
                </span>
                <span className="text-gray-500">Etapa {step + 1} de 3</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-gray-100">
                <div
                  className="h-full rounded-full bg-purple-600 transition-all"
                  style={{ width: `${((step + 1) / 3) * 100}%` }}
                />
              </div>
              {continuingPublicAssessment && step === 0 && (
                <div className="mt-4 rounded-xl border border-indigo-200 bg-indigo-50 p-4 text-sm leading-6 text-indigo-900">
                  <strong>
                    Suas respostas anteriores já foram aproveitadas.
                  </strong>{" "}
                  Confirme somente estas informações adicionais para
                  personalizar o plano da clínica.
                </div>
              )}
              <p className="mt-3 text-sm text-gray-500">
                {steps[step]} · você poderá alterar estas respostas depois.
              </p>
            </div>

            {step === 0 && (
              <div className="space-y-5">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Como o serviço está organizado?
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    Escolha a opção que mais se aproxima da realidade atual.
                  </p>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {[
                    ["individual_office", "Consultório individual"],
                    ["clinic", "Clínica odontológica"],
                    ["collective_office", "Consultório coletivo"],
                    ["mobile_service", "Atendimento móvel ou portátil"],
                  ].map(([value, label]) => (
                    <Choice
                      key={value}
                      active={profile.establishment_type === value}
                      title={label}
                      onClick={() =>
                        setProfile({ ...profile, establishment_type: value })
                      }
                    />
                  ))}
                </div>
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">
                    Quantos consultórios individuais existem?
                  </span>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={profile.consultory_count}
                    onChange={(e) =>
                      setProfile({
                        ...profile,
                        consultory_count: e.target.value,
                      })
                    }
                    className="mt-2 w-32 rounded-lg border border-gray-300 px-3 py-2 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-100"
                  />
                </label>
                <div className="rounded-xl border border-gray-200 p-4">
                  <p className="font-medium text-gray-900">
                    Atendimento odontológico geral
                  </p>
                  <p className="mt-1 text-sm leading-6 text-gray-500">
                    Este é o perfil da maioria dos consultórios e clínicas. Você
                    só precisa abrir os roteiros especiais se trabalhar com
                    cirurgia estética orofacial ou em serviço público.
                  </p>
                  {!showSpecialRoutes ? (
                    <button
                      type="button"
                      onClick={() => setShowSpecialRoutes(true)}
                      className="mt-3 text-sm font-medium text-purple-700"
                    >
                      Minha clínica usa um roteiro especial
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setShowSpecialRoutes(false);
                        setProfile({
                          ...profile,
                          cfo_service_type: "standard",
                        });
                      }}
                      className="mt-3 text-sm font-medium text-gray-600"
                    >
                      Usar atendimento odontológico geral
                    </button>
                  )}
                </div>
                {showSpecialRoutes && (
                  <div>
                    <p className="mb-3 text-sm font-medium text-gray-700">
                      Selecione o roteiro especial
                    </p>
                    <div className="grid gap-3 sm:grid-cols-2">
                      {[
                        [
                          "hof_type_i",
                          "HOF/CEOF — Ambiente Tipo I",
                          "Procedimentos de pequeno porte, somente com anestesia local.",
                        ],
                        [
                          "hof_type_ii",
                          "CEOF — Ambiente Tipo II",
                          "Procedimentos de médio porte, com estrutura própria para sedação.",
                        ],
                        [
                          "hof_type_iii",
                          "CEOF — Ambiente Tipo III",
                          "Procedimentos de maior porte em estabelecimento pessoa jurídica.",
                        ],
                        [
                          "public_ubs",
                          "Serviço público — UBS",
                          "Unidade Básica de Saúde com equipe de Saúde Bucal.",
                        ],
                        [
                          "public_ceo",
                          "Serviço público — CEO",
                          "Centro de Especialidades Odontológicas.",
                        ],
                        [
                          "public_other",
                          "Outro serviço público",
                          "Outro estabelecimento odontológico público.",
                        ],
                      ].map(([value, title, description]) => (
                        <Choice
                          key={value}
                          active={profile.cfo_service_type === value}
                          title={title}
                          description={description}
                          onClick={() =>
                            setProfile({
                              ...profile,
                              cfo_service_type: value,
                              cfo_inspection_type:
                                value === "hof_type_iii" ||
                                value.startsWith("public_")
                                  ? "legal_entity"
                                  : profile.cfo_inspection_type,
                            })
                          }
                        />
                      ))}
                    </div>
                    <p className="mt-2 text-xs leading-5 text-gray-400">
                      Estes roteiros só se aplicam aos serviços indicados.
                    </p>
                  </div>
                )}
                {!profile.cfo_service_type?.startsWith("public_") &&
                  profile.cfo_service_type !== "hof_type_iii" && (
                    <div>
                      <p className="mb-3 text-sm font-medium text-gray-700">
                        Como o serviço está registrado no CRO?
                      </p>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <Choice
                          active={
                            profile.cfo_inspection_type ===
                            "individual_professional"
                          }
                          title="Pessoa física"
                          description="Consultório registrado em nome do cirurgião-dentista."
                          onClick={() =>
                            setProfile({
                              ...profile,
                              cfo_inspection_type: "individual_professional",
                            })
                          }
                        />
                        <Choice
                          active={
                            profile.cfo_inspection_type === "legal_entity"
                          }
                          title="Pessoa jurídica"
                          description="Clínica ou consultório com registro de pessoa jurídica no CRO."
                          onClick={() =>
                            setProfile({
                              ...profile,
                              cfo_inspection_type: "legal_entity",
                            })
                          }
                        />
                      </div>
                      <p className="mt-2 text-xs leading-5 text-gray-400">
                        Essa resposta seleciona também os itens PF ou PJ do
                        Manual de Fiscalização.
                      </p>
                    </div>
                  )}
                {profile.cfo_service_type === "hof_type_iii" && (
                  <div className="rounded-xl bg-indigo-50 p-4 text-sm text-indigo-800">
                    O roteiro do Ambiente Tipo III é destinado a estabelecimento
                    pessoa jurídica. Essa natureza será aplicada
                    automaticamente.
                  </div>
                )}
              </div>
            )}

            {step === 1 && (
              <div className="space-y-7">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Quais recursos são utilizados?
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    Isso define somente os itens aplicáveis ao plano.
                  </p>
                </div>
                <div>
                  <p className="mb-3 text-sm font-medium text-gray-700">
                    Sedação realizada no estabelecimento
                  </p>
                  <div className="grid gap-3 sm:grid-cols-3">
                    {[
                      ["none", "Não realiza"],
                      ["inhaled", "Inalatória"],
                      ["intravenous", "Endovenosa"],
                    ].map(([value, label]) => (
                      <Choice
                        key={value}
                        active={profile.sedation_type === value}
                        title={label}
                        onClick={() =>
                          setProfile({ ...profile, sedation_type: value })
                        }
                      />
                    ))}
                  </div>
                </div>
                <div>
                  <p className="mb-3 text-sm font-medium text-gray-700">
                    Radiologia odontológica
                  </p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {[
                      ["none", "Não utiliza"],
                      ["intraoral", "Somente intraoral"],
                      ["extraoral", "Somente extraoral"],
                      ["both", "Intraoral e extraoral"],
                    ].map(([value, label]) => (
                      <Choice
                        key={value}
                        active={profile.radiology_type === value}
                        title={label}
                        onClick={() =>
                          setProfile({ ...profile, radiology_type: value })
                        }
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-7">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Só mais três informações
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    Depois disso, mostraremos apenas o que merece atenção.
                  </p>
                </div>
                <div>
                  <p className="mb-3 text-sm font-medium text-gray-700">
                    Processamento dos instrumentais
                  </p>
                  <div className="grid gap-3 sm:grid-cols-3">
                    {[
                      ["internal", "Na própria clínica"],
                      ["outsourced", "Terceirizado"],
                      ["not_applicable", "Não se aplica"],
                    ].map(([value, label]) => (
                      <Choice
                        key={value}
                        active={profile.processing_type === value}
                        title={label}
                        onClick={() =>
                          setProfile({ ...profile, processing_type: value })
                        }
                      />
                    ))}
                  </div>
                </div>
                {profile.processing_type === "internal" && (
                  <Choice
                    active={profile.autoclave_has_vacuum_pump}
                    title="A autoclave possui bomba de vácuo"
                    description="Marque somente se tiver certeza; isso altera o monitoramento diário aplicável."
                    onClick={() =>
                      setProfile({
                        ...profile,
                        autoclave_has_vacuum_pump:
                          !profile.autoclave_has_vacuum_pump,
                      })
                    }
                  />
                )}
                <Choice
                  active={profile.provides_external_care}
                  title="Realiza atendimento fora do estabelecimento"
                  onClick={() =>
                    setProfile({
                      ...profile,
                      provides_external_care: !profile.provides_external_care,
                    })
                  }
                />
                <Choice
                  active={profile.has_prosthesis_lab}
                  title="Possui laboratório de prótese vinculado"
                  onClick={() =>
                    setProfile({
                      ...profile,
                      has_prosthesis_lab: !profile.has_prosthesis_lab,
                    })
                  }
                />
              </div>
            )}

            <div className="mt-8 flex justify-between gap-3 border-t pt-5">
              <button
                type="button"
                disabled={step === 0}
                onClick={() => setStep(step - 1)}
                className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 disabled:invisible"
              >
                Voltar
              </button>
              {step < 2 ? (
                <button
                  type="button"
                  disabled={step === 0 && !profile.cfo_inspection_type}
                  onClick={() => setStep(step + 1)}
                  className="flex items-center gap-2 rounded-lg bg-purple-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Continuar <ArrowRight className="h-4 w-4" />
                </button>
              ) : (
                <button
                  type="button"
                  disabled={saving || !profile.cfo_inspection_type}
                  onClick={saveProfile}
                  className="flex items-center gap-2 rounded-lg bg-purple-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-purple-700 disabled:opacity-60"
                >
                  <Sparkles className="h-4 w-4" />{" "}
                  {saving ? "Preparando..." : "Preparar meu plano"}
                </button>
              )}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!data?.is_configured)
    return (
      <Layout>
        <div className="mx-auto max-w-4xl py-6 sm:py-10">
          <div className="overflow-hidden rounded-2xl bg-gradient-to-br from-purple-700 to-indigo-700 p-6 text-white shadow-sm sm:p-10">
            <div className="max-w-2xl">
              <span className="inline-flex rounded-full bg-white/15 px-3 py-1 text-xs font-semibold">
                {data?.pending_assessment
                  ? "Diagnóstico gratuito localizado"
                  : "RDC Anvisa nº 1.002/2025 · Fiscalização CFO 2026"}
              </span>
              <h2 className="mt-5 text-2xl font-bold sm:text-3xl">
                {data?.pending_assessment
                  ? "Continue de onde parou"
                  : "Prepare a clínica para a RDC e para o novo roteiro do CFO"}
              </h2>
              <p className="mt-3 text-purple-100">
                {data?.pending_assessment
                  ? `Aproveitamos as respostas da avaliação pública${data.pending_assessment.level_label ? `, classificada como ${data.pending_assessment.level_label.toLowerCase()}` : ""}. Confirme agora apenas as informações necessárias para preparar o plano da clínica e selecionar o roteiro PF ou PJ.`
                  : "Responda um diagnóstico curto. O OdontoAssist organizará as ações de Boas Práticas e selecionará o roteiro de fiscalização PF ou PJ aplicável à clínica."}
              </p>
              <div className="mt-6 grid gap-3 text-sm sm:grid-cols-3">
                {(data?.pending_assessment
                  ? [
                      "Respostas aproveitadas",
                      "Confirmação rápida",
                      "Plano personalizado",
                    ]
                  : [
                      "Cerca de 3 minutos",
                      "Plano personalizado",
                      "Progresso salvo",
                    ]
                ).map((label) => (
                  <span key={label} className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4" />
                    {label}
                  </span>
                ))}
              </div>
              {canManage ? (
                <button
                  type="button"
                  onClick={startWizard}
                  className="mt-7 rounded-lg bg-white px-5 py-3 font-semibold text-purple-700 hover:bg-purple-50"
                >
                  {data?.pending_assessment
                    ? "Continuar de onde parei"
                    : "Começar diagnóstico"}
                </button>
              ) : (
                <p className="mt-7 rounded-lg bg-white/10 p-3 text-sm">
                  O administrador da clínica pode iniciar o diagnóstico. Depois,
                  você poderá acompanhar o plano.
                </p>
              )}
            </div>
          </div>
          <p className="mt-4 text-center text-xs text-gray-500">
            Ferramenta de apoio. A validação permanece sob responsabilidade do
            RT e da autoridade sanitária.
          </p>
        </div>
      </Layout>
    );

  const nextItem = data.next_item;
  return (
    <Layout>
      <div className="mx-auto max-w-5xl space-y-5 py-2">
        <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
          <div>
            <div className="flex items-center gap-2 text-sm font-medium text-purple-700">
              <ShieldCheck className="h-4 w-4" /> RDC Anvisa nº 1.002/2025
            </div>
            <h2 className="mt-1 text-2xl font-bold text-gray-900">
              Boas Práticas da clínica
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Uma ação por vez. O plano completo fica disponível quando você
              precisar.
            </p>
          </div>
          {canManage && (
            <button
              type="button"
              onClick={startWizard}
              className="flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Edit3 className="h-4 w-4" /> Revisar diagnóstico
            </button>
          )}
        </div>

        <div className="grid gap-4 lg:grid-cols-[1fr_280px]">
          <section className="rounded-2xl bg-white p-5 shadow-sm sm:p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">
                  Próxima ação recomendada
                </p>
                {nextItem ? (
                  <>
                    <h3 className="mt-2 text-xl font-semibold text-gray-900">
                      {nextItem.title}
                    </h3>
                    <p className="mt-2 text-sm leading-6 text-gray-600">
                      {nextItem.description}
                    </p>
                    <div className="mt-4 rounded-xl bg-purple-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-purple-700">
                        Comece assim
                      </p>
                      <p className="mt-2 text-sm leading-6 text-gray-800">
                        {nextItem.first_step}
                      </p>
                      <p className="mt-2 text-xs leading-5 text-gray-600">
                        <strong>Quem normalmente participa:</strong>{" "}
                        {nextItem.owner_label}
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <h3 className="mt-2 text-xl font-semibold text-emerald-700">
                      Plano revisado
                    </h3>
                    <p className="mt-2 text-sm text-gray-600">
                      Todos os itens foram marcados como concluídos ou não
                      aplicáveis.
                    </p>
                  </>
                )}
              </div>
              <div className="rounded-xl bg-purple-50 p-3 text-purple-700">
                <ClipboardCheck className="h-6 w-6" />
              </div>
            </div>
            {nextItem && canManage && (
              <div className="mt-5 flex flex-wrap gap-2">
                {STATUS_OPTIONS.slice(1, 3).map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => updateStatus(nextItem, option.value)}
                    className={`rounded-lg px-3 py-2 text-sm font-medium ${option.className}`}
                  >
                    {option.label}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => openActionModule(nextItem.odontoassist_module)}
                  className="rounded-lg border border-purple-200 bg-white px-3 py-2 text-sm font-medium text-purple-700"
                >
                  {nextItem.odontoassist_module_label}
                </button>
              </div>
            )}
            {nextItem && !canManage && (
              <button
                type="button"
                onClick={() => openActionModule(nextItem.odontoassist_module)}
                className="mt-5 rounded-lg border border-purple-200 bg-white px-3 py-2 text-sm font-medium text-purple-700"
              >
                {nextItem.odontoassist_module_label}
              </button>
            )}
          </section>
          <aside className="rounded-2xl bg-white p-5 shadow-sm">
            <div className="flex items-end justify-between">
              <span className="text-sm font-medium text-gray-600">
                Progresso
              </span>
              <span className="text-2xl font-bold text-purple-700">
                {data.progress}%
              </span>
            </div>
            <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-gray-100">
              <div
                className="h-full rounded-full bg-purple-600"
                style={{ width: `${data.progress}%` }}
              />
            </div>
            <p className="mt-3 text-xs text-gray-500">
              {data.completed_items} de {data.total_items} itens revisados
            </p>
            <div className="mt-5 flex items-center gap-2 border-t pt-4 text-xs text-gray-500">
              <Clock3 className="h-4 w-4" /> Prazo de adequação: 11/12/2026
            </div>
          </aside>
        </div>

        <InspectionSummaryPanel />

        <section
          id="compliance-controls"
          className="scroll-mt-20 rounded-2xl bg-white shadow-sm"
        >
          <button
            type="button"
            onClick={() => setShowPlan(!showPlan)}
            className="flex w-full items-center justify-between gap-4 p-5 text-left sm:p-6"
          >
            <div>
              <h3 className="font-semibold text-gray-900">Plano completo</h3>
              <p className="mt-1 text-sm text-gray-500">
                Consulte ou atualize os demais itens quando desejar.
              </p>
            </div>
            {showPlan ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </button>
          {showPlan && (
            <div className="divide-y border-t">
              {data.checklist.map((item) => {
                const current =
                  STATUS_OPTIONS.find(
                    (option) => option.value === item.status,
                  ) || STATUS_OPTIONS[0];
                return (
                  <div key={item.id} className="p-5 sm:px-6">
                    <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
                      <div className="min-w-0">
                        <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                          {CATEGORY_LABELS[item.category]}
                        </p>
                        <h4 className="mt-1 font-medium text-gray-900">
                          {item.title}
                        </h4>
                        <p className="mt-1 text-sm text-gray-500">
                          {item.description}
                        </p>
                      </div>
                      {canManage ? (
                        <select
                          value={item.status}
                          onChange={(event) =>
                            updateStatus(item, event.target.value)
                          }
                          className="shrink-0 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
                        >
                          {STATUS_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span
                          className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium ${current.className}`}
                        >
                          {current.label}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
        <CFOInspectionPreparationPanel
          canManage={canManage}
          onProfileReview={startWizard}
        />
        <section className="rounded-2xl bg-white shadow-sm">
          <button
            type="button"
            onClick={() => setShowOtherControls(!showOtherControls)}
            className="flex w-full items-center justify-between gap-4 p-5 text-left sm:p-6"
          >
            <div>
              <h3 className="font-semibold text-gray-900">
                Documentos, registros e outras ferramentas
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Abra somente quando precisar registrar uma fiscalização,
                organizar a equipe ou consultar os controles da clínica.
              </p>
            </div>
            {showOtherControls ? (
              <ChevronUp className="h-5 w-5 shrink-0 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 shrink-0 text-gray-400" />
            )}
          </button>
          {showOtherControls && (
            <div className="space-y-4 border-t bg-gray-50/50 p-4 sm:p-5">
              <ReceivedInspectionsPanel canManage={canManage} />
              <TeamAdvertisingPanel canManage={canManage} />
              <ComplianceModulesHub
                key={focusedModuleRequest}
                canManage={canManage}
                initialSelected={focusedModule}
                processingType={data?.profile?.processing_type}
                hasVacuumPump={
                  data?.profile?.autoclave_has_vacuum_pump === true
                }
              />
            </div>
          )}
        </section>
        <div className="flex items-start gap-2 rounded-xl bg-blue-50 p-4 text-xs leading-5 text-blue-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> {data.disclaimer}
        </div>
      </div>
    </Layout>
  );
};

export default CompliancePage;
